import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Loader2, CheckCircle, XCircle, Eye, EyeOff, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const SetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [validating, setValidating] = useState(true);
  const [valid, setValid] = useState(false);
  const [error, setError] = useState('');
  const [userInfo, setUserInfo] = useState(null);
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    if (!token) {
      setValidating(false);
      setValid(false);
      setError('No token provided');
      return;
    }

    try {
      const res = await api.get(`/auth/validate-set-password-token?token=${token}`);
      if (res.data.valid) {
        setValid(true);
        setUserInfo(res.data);
      } else {
        setValid(false);
        setError(res.data.error || 'Invalid link');
      }
    } catch (e) {
      setValid(false);
      setError('Failed to validate link');
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post('/auth/set-password', { token, password });
      
      // Store token and redirect
      localStorage.setItem('token', res.data.token);
      toast.success('Password set successfully! Welcome to iEvntür!');
      
      // Redirect based on role
      const role = res.data.role;
      const adminRole = res.data.admin_role;
      
      if (role === 'admin' && adminRole) {
        // Admin user - go to admin home
        navigate('/admin/home');
      } else if (role === 'client') {
        navigate('/client/home');
      } else {
        // Talent user - go to profile completion
        navigate('/talent/profile-info');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to set password');
    } finally {
      setSubmitting(false);
    }
  };

  // Password strength indicator
  const getPasswordStrength = () => {
    if (!password) return { strength: 0, label: '', color: '' };
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    
    if (strength <= 2) return { strength, label: 'Weak', color: 'bg-red-500' };
    if (strength <= 3) return { strength, label: 'Fair', color: 'bg-yellow-500' };
    if (strength <= 4) return { strength, label: 'Good', color: 'bg-green-500' };
    return { strength, label: 'Strong', color: 'bg-green-600' };
  };

  const passwordStrength = getPasswordStrength();

  if (validating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-black flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white/95 backdrop-blur">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-cyan-500 mb-4" />
            <p className="text-gray-600">Validating your link...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!valid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-black flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white/95 backdrop-blur">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <XCircle className="w-12 h-12 text-red-500 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Invalid Link</h2>
            <p className="text-gray-600 text-center mb-6">{error}</p>
            <p className="text-sm text-gray-500 text-center">
              Please contact <a href="mailto:support@ievntur.com" className="text-cyan-500 hover:underline">support@ievntur.com</a> for assistance.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-black flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-white/95 backdrop-blur" data-testid="set-password-card">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4">
            <div className="w-16 h-16 bg-gradient-to-r from-cyan-500 to-fuchsia-500 rounded-full flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
          </div>
          <CardTitle className="text-2xl">Welcome to iEvntür</CardTitle>
          <CardDescription>
            {userInfo?.first_name ? `Hi ${userInfo.first_name}! ` : ''}Set your password to get started
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input 
                value={userInfo?.email || ''} 
                disabled 
                className="bg-gray-50"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Create Password</Label>
              <div className="relative">
                <Input 
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  className="pr-10"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {password && (
                <div className="space-y-1">
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map(i => (
                      <div 
                        key={i} 
                        className={`h-1 flex-1 rounded ${i <= passwordStrength.strength ? passwordStrength.color : 'bg-gray-200'}`}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-gray-500">Password strength: {passwordStrength.label}</p>
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <Label>Confirm Password</Label>
              <Input 
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Re-enter your password"
                data-testid="confirm-password-input"
              />
              {confirmPassword && password !== confirmPassword && (
                <p className="text-xs text-red-500">Passwords do not match</p>
              )}
            </div>

            <Button 
              type="submit" 
              className="w-full bg-gradient-to-r from-cyan-500 to-fuchsia-500 hover:from-cyan-600 hover:to-fuchsia-600"
              disabled={submitting || password.length < 8 || password !== confirmPassword}
              data-testid="set-password-btn"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Setting Password...</>
              ) : (
                <>Set Password & Continue <ArrowRight className="w-4 h-4 ml-2" /></>
              )}
            </Button>
          </form>

          <div className="mt-6 p-4 bg-cyan-50 rounded-lg">
            {userInfo?.role === 'admin' ? (
              <>
                <h4 className="font-medium text-cyan-900 text-sm mb-2">After setting your password:</h4>
                <ul className="text-sm text-cyan-700 space-y-1">
                  <li className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-cyan-100 text-cyan-600 flex items-center justify-center text-xs">1</span>
                    Access the Admin Dashboard
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-cyan-100 text-cyan-600 flex items-center justify-center text-xs">2</span>
                    Review your role permissions
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-cyan-100 text-cyan-600 flex items-center justify-center text-xs">3</span>
                    Start managing the platform
                  </li>
                </ul>
                {userInfo?.admin_role && (
                  <div className="mt-3 pt-3 border-t border-cyan-200">
                    <p className="text-sm text-cyan-800">
                      Your role: <span className="font-semibold capitalize">{userInfo.admin_role.replace('_', ' ')}</span>
                    </p>
                  </div>
                )}
              </>
            ) : (
              <>
                <h4 className="font-medium text-cyan-900 text-sm mb-2">After setting your password:</h4>
                <ul className="text-sm text-cyan-700 space-y-1">
                  <li className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-cyan-100 text-cyan-600 flex items-center justify-center text-xs">1</span>
                    Select your roles
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-cyan-100 text-cyan-600 flex items-center justify-center text-xs">2</span>
                    Confirm your address
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-cyan-100 text-cyan-600 flex items-center justify-center text-xs">3</span>
                    Upload a profile photo
                  </li>
                </ul>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SetPassword;
